import type { ProductTagListItem } from "./ProductTagList";

/**
 * Structural shape of one `product_tags` row. Declared here rather than
 * imported from the catalog domain: this composite is client-side and must
 * stay free of server-only domain code, and a structural type keeps
 * `CatalogProductTag[]` assignable without the coupling.
 */
export interface ProductTagSource {
	kind: string;
	title: string;
	label?: string;
	code?: string;
	icon?: string;
	appliesTo?: string;
}

const LISTED_KINDS = ["guarantee", "care", "dietary"];

const ICON_BY_CODE: Record<string, string> = {
	"care-dishwasher-safe": "droplet",
	"care-bpa-free": "shield",
	"care-food-safe": "check-circle",
	"care-microwave-safe": "zap",
	"care-freezer-safe": "cloud-snow",
	"care-oven-safe": "thermometer",
	"care-prop-65": "alert-triangle"
};

const ICON_BY_KIND: Record<string, string> = {
	guarantee: "guarantee",
	care: "check-circle",
	dietary: "check-circle"
};

/**
 * The guarantee, care and dietary tags that render as one row of product
 * facts. Highlight tags are promotional and render separately — see
 * `ProductHighlightTag`.
 */
export function selectProductTags(
	productTags: ProductTagSource[] | undefined
): ProductTagListItem[] {
	return (productTags ?? [])
		.filter((tag) => LISTED_KINDS.includes(tag.kind))
		.map((tag) => ({
			label: toLabel(tag),
			icon: toIcon(tag)
		}));
}

// "Brownie Pan: 3-Year guarantee" — the CMS names `applies_to` as a prefix.
function toLabel(tag: ProductTagSource): string {
	const part = tag.appliesTo?.trim();
	const text = tag.label?.trim() || tag.title;
	return part ? `${part}: ${text}` : text;
}

function toIcon(tag: ProductTagSource): string | undefined {
	const authored = toDesignSystemIconName(tag.icon);
	if (authored) return authored;
	if (tag.code && ICON_BY_CODE[tag.code]) return ICON_BY_CODE[tag.code];
	return ICON_BY_KIND[tag.kind];
}

// Guarantee entries author icon-set tokens ("icon-award", "none"); DS icon
// names are bare, and an unknown name renders as a blank space.
function toDesignSystemIconName(icon: string | undefined): string | undefined {
	const name = icon?.trim().replace(/^icon-/, "");
	return name && name !== "none" ? name : undefined;
}
